import { FC, useCallback, useMemo } from 'react';

import { cn, convertToAmount, useAdminParams } from 'utils/helpers';
import { Link } from 'react-router-dom';
import { useAppWrapperContext } from 'ui/layouts/AppWrapper';

import { useGetDeals } from '../hooks';

interface PAYLOAD {
  leadId?: string;
  companyId?: string;
}

interface PROPS {
  payload: PAYLOAD;
}

const ContactDeals: FC<PROPS> = ({ payload }) => {
  const { data: deals } = useGetDeals(payload);

  const {
    state: { isPreview },
  } = useAppWrapperContext();
  const { accountId, accountUserId } = useAdminParams();

  const link = useCallback(
    (deal: Record<string, string | number>) => {
      if (!isPreview) return `/sales/deals/${deal._id}`;

      return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${deal._id}`;
    },
    [isPreview, accountId, accountUserId],
  );

  const viewAllLink = useMemo(() => {
    if (!isPreview) return `/sales/deals`;

    return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals`;
  }, [isPreview, accountId, accountUserId]);

  return (
    <div>
      <div
        className={cn(
          'text-xs pt-5 mt-[18px]',
          'border-t-[0.5px] border-separator-primary',
          'flex flex-col space-y-3',
        )}
      >
        {!deals?.data?.length ? (
          <p className="text-xs">See the Deals attached to this record</p>
        ) : (
          deals?.data?.map((deal: Record<string, any>) => (
            <div
              key={deal._id}
              className={cn(
                'bg-fill-secondary',
                'border',
                'text-xs',
                'border-separator-secondary',
                'rounded-[6px]',
                'pt-[22px]',
                'pb-[18px]',
                'px-[22px]',
                'space-y-2',
              )}
            >
              <div>
                <Link
                  className={cn(
                    'leading-[22px]',
                    'text-sm',
                    'font-semibold',
                    'text-info-base',
                    'break-all',
                    'tracking-[0.04em]',
                  )}
                  to={link(deal)}
                >
                  {deal?.name}
                </Link>
                <p className="break-all text-xs mt-2">
                  Amount:
                  <span className="ml-0.5">
                    ${convertToAmount(deal?.amount)}
                  </span>
                </p>
                <p className="break-all text-xs">
                  Stage:
                  <span className="ml-0.5">{deal?.stageId?.name}</span>
                </p>
              </div>
            </div>
          ))
        )}
      </div>
      {!deals?.data?.length ? null : (
        <div className="text-center mt-2">
          <Link
            className="leading-[22px] text-sm font-semibold tracking-[0.04em]"
            to={viewAllLink}
          >
            View all deals
          </Link>
        </div>
      )}
    </div>
  );
};

export default ContactDeals;
